export interface AppErrorDetail {
  code: string;
  message: string;
}

export class AppError extends Error {
  readonly resource?: string;
  readonly status?: number;
  readonly details: AppErrorDetail[];

  constructor(params: { message: string; resource?: string; status?: number; details?: AppErrorDetail[] }) {
    super(params.message);

    this.resource = params.resource;
    this.status = params.status;
    this.details = params.details ?? [];
  }
}

export interface RouteErrorMessage {
  title: string;
  description: string;
  status?: number;
}

export function badRequest(resource?: string, details?: AppErrorDetail[]) {
  return new AppError({
    message: "Bad request",
    status: 400,
    resource,
    details,
  });
}

export function unauthorized(resource?: string, details?: AppErrorDetail[]) {
  return new AppError({
    message: "Unauthorized",
    status: 401,
    resource,
    details,
  });
}

export function forbidden(resource?: string, details?: AppErrorDetail[]) {
  return new AppError({
    message: "Forbidden",
    status: 403,
    resource,
    details,
  });
}

export function notFound(resource?: string, details?: AppErrorDetail[]) {
  return new AppError({
    message: "Not found",
    status: 404,
    resource,
    details,
  });
}

export function conflict(resource?: string, details?: AppErrorDetail[]) {
  return new AppError({
    message: "Conflict",
    status: 409,
    resource,
    details,
  });
}

export function tooManyRequests(resource?: string, details?: AppErrorDetail[]) {
  return new AppError({
    message: "Too many requests",
    status: 429,
    resource,
    details,
  });
}

export function internalServerError(resource?: string, details?: AppErrorDetail[]) {
  return new AppError({
    message: "Server error has occurred",
    status: 500,
    resource,
    details,
  });
}

export function noNetworkConnection(resource?: string, details?: AppErrorDetail[]) {
  return new AppError({
    message: "Network error",
    status: 503,
    resource,
    details,
  });
}

export function getRouteErrorMessage(error: unknown): RouteErrorMessage {
  if (error instanceof AppError) {
    return {
      title: "Something went wrong",
      description: error.details[0]?.message ?? error.message,
      status: error.status,
    };
  }

  if (error instanceof Error) {
    return {
      title: "Something went wrong",
      description: error.message,
    };
  }

  return {
    title: "Something went wrong",
    description: "An unexpected error occurred. Please try again or contact support if the problem persists.",
  };
}
